/* eslint-disable no-console */
// ** React imports
import React, { useCallback, useEffect, useRef, useState } from 'react';

// ** Redux imports
import { useDispatch, useSelector } from 'react-redux';

// ** Third Party imports
import { Call, Device as TwilioDevice } from '@twilio/voice-sdk';

// ** Custom imports
import {
  clearDuringCallNote,
  clearInitiatedCallId,
  enableCallRecording,
  resetGetCallLogsProspectProfileSectionRequest,
  setCallNetworkLevel,
  setCurrentCallManagementState,
  setInitiatedCallId,
  setShowDialer,
  setShowIncomingCall,
  setShowMicrophoneAccessWarningModal,
} from '../../../components/dialer/dialer-slice';
import {
  assignCallOutcomeRequest,
  createCallLogsNoteRequest,
  createTwillioVoiceTokenRequest,
  endInitiatedCallRequest,
  getCallLogsProspectProfileSectionRequest,
  getDialerPhoneNumberListRequest,
  initiateCallRequest,
  pickupCallRequest,
} from '../../../components/dialer/extra-actions';
import { visibility } from '../../../components/dialer/types';
import {
  EndInitiatedCallPayload,
  InitiateCallPayload,
} from '../../../components/dialer/types/manual-call-dialing';
import { useFirebaseData } from '../../../components/firebase';
import { useAuth } from '../../../components/firebase/auth';
import { hashUserId as hashingCallId } from '../../../components/firebase/firebase-auth';
import { RootState } from '../../../store/root-reducer';
import toaster from '../../toaster';
import { DialerIncomingCallWrapper } from './components/dialer-incoming-call-wrapper';
import DialerPopup from './components/dialer-popup';
import {
  CALL_MANAGEMENT_STATE_INITIAL as callManagementStateInitial,
  DialerPhoneNumberCallStatusLabel,
  isOngoingDialerCall,
} from './constants';
import usePreCallTest from './hooks/usePreCallTest';
import useTwilioHold from './hooks/useTwilioHold';
import {
  CallManagementState,
  DialerPhoneNumberCallStatusEnum,
  IncomingCallProspect,
} from './types';

import { RequestStatus } from '../../enums/request-status';
import { executeOnRequestStatus } from '../../utils';
import {
  registerActiveTwilioCall,
  registerTwilioDevice,
} from '../../utils/twilio-device-singleton';
import PipWindow from '../pip-window';
import ConfirmationModal from '../confirmation-modal';
import PostCallOutcomeModal from './components/post-call-outcome-modal/post-call-outcome-modal-container';
import MicrophoneAccessWarningModal from './components/pre-call-test/microphone-access-warning-modal';
import PreCallTestModal from './components/pre-call-test/pre-call-test-modal';
import { warningToLevel } from './utils/helper';

type PostCallData = {
  callerName: string | null;
  callerPhone: string;
  isIncomingCall: boolean;
  callViaLabel: string | null;
  callViaNumber: string;
  wasRecording: boolean;
  completedAt: string | null;
  callDuration: number | null;
  prospectId?: number | null;
};

const DialerManager: React.FC = () => {
  const dispatch = useDispatch();

  const twilioDeviceRef = useRef<TwilioDevice | null>(null);
  const showDialerRef = useRef(false);
  const pendingCallsRef = useRef<{ outcome: boolean; note: boolean }>({
    outcome: false,
    note: false,
  });
  const callIdRef = useRef<string | number | null>(null);

  // ** Local State **
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const [showWithoutPip, setShowWithoutPip] = useState(false);
  const [showPostCallModal, setShowPostCallModal] = useState<number | null>(
    null,
  );
  const [showEndCallConfirmModal, setShowEndCallConfirmModal] = useState(false);
  const [pipClosedUnexpectedly, setPipClosedUnexpectedly] = useState(false);
  const [callConnectedAt, setCallConnectedAt] = useState<number | null>(null);
  const [postCallData, setPostCallData] = useState<PostCallData | null>(null);

  const [
    incomingCallProspect,
    setIncomingCallProspect,
  ] = useState<IncomingCallProspect | null>(null);
  const [
    callManagementState,
    setCallManagementState,
  ] = useState<CallManagementState>(callManagementStateInitial);
  const [isCallConnected, setIsCallConnected] = useState(false);

  const [showPreCallTestModal, setShowPreCallTestModal] = useState(false);
  const preCallTestProps = usePreCallTest(showPreCallTestModal);

  const playOutboundRingtone = useCallback(() => {
    const device = twilioDeviceRef.current;
    if (device) {
      // eslint-disable-next-line no-underscore-dangle
      (device as any)?._soundcache?.get('incoming')?.play();
    }
  }, []);

  const stopOutboundRingtone = useCallback(() => {
    const device = twilioDeviceRef.current;
    if (device) {
      // eslint-disable-next-line no-underscore-dangle
      (device as any)._soundcache?.get('incoming')?.stop();
    }
  }, []);

  const holdCallHandlers = useTwilioHold(
    twilioDeviceRef?.current,
    callManagementState?.call,
  );

  // ** Redux State **
  const twillioVoiceToken = useSelector(
    (state: RootState) => state.dialer?.twillioVoiceToken || null,
  );
  // Redux state
  const showDialer = useSelector(
    (state: RootState) => state.dialer?.showDialer,
  );
  const showIncomingCall = useSelector(
    (state: RootState) => state.dialer?.showIncomingCall,
  );
  const twillioVoiceTokenSessionHashId = useSelector(
    (state: RootState) => state.dialer?.twillioVoiceTokenSessionHashId || null,
  );
  const showMicrophoneAccessWarningModal = useSelector(
    (state: RootState) =>
      state.dialer?.showMicrophoneAccessWarningModal || false,
  );
  const initiatedCallId = useSelector(
    (state: RootState) => state.dialer?.initiatedCallId || null,
  );
  const firebaseConnectionDisabledByUser = useSelector(
    (state: RootState) =>
      state?.dialer?.firebaseConnectionDisabledByUser ?? false,
  );
  const currentCallManagementState = useSelector(
    (state: RootState) => state?.dialer?.currentCallManagementState,
  );
  const duringCallNote = useSelector(
    (state: RootState) => state?.dialer?.duringCallNote,
  );

  const dialerPhoneNumberList = useSelector(
    (state: RootState) => state.dialer?.dialerPhoneNumberList ?? [],
  );
  const selectedCallbackProspect = useSelector(
    (state: RootState) => state?.dialer?.selectedCallbackProspect ?? [],
  );
  const endInitiatedCallRequestStatus = useSelector(
    (state: RootState) => state?.dialer?.endInitiatedCallRequest?.status,
  );
  const initiateCallRequestStatus = useSelector(
    (state: RootState) => state?.dialer?.initiateCallRequest?.status,
  );
  const dialerControlsState = useSelector(
    (state: RootState) => state?.dialer?.dialerControlsState,
  );
  const outcomeList = useSelector(
    (state: RootState) => state?.dialer?.getCallOutcomeListResponse ?? [],
  );
  const profileSectionRequestStatus = useSelector(
    (state: RootState) =>
      state?.dialer?.getCallLogsProspectProfileSectionRequest?.status,
  );
  const profileSectionResponse = useSelector(
    (state: RootState) =>
      state?.dialer?.getCallLogsProspectProfileSectionResponse,
  );
  const assignCallOutcomeStatus = useSelector(
    (state: RootState) => state?.dialer?.assignCallOutcomeRequest?.status,
  );
  const createNoteStatus = useSelector(
    (state: RootState) => state?.dialer?.createCallLogsNoteRequest?.status,
  );

  const shouldConnectFirebase =
    Boolean(initiatedCallId) && !firebaseConnectionDisabledByUser;

  // ** Firebase Implementation **
  const { uid } = useAuth();
  const { data: firebaseData, isConnectionPending } = useFirebaseData(
    uid,
    shouldConnectFirebase ? hashingCallId(initiatedCallId) : null,
    'dialer_calls',
    shouldConnectFirebase,
  );

  // Handlers
  const handleCallClick = (payload: InitiateCallPayload) => {
    holdCallHandlers?.cleanupHold?.();
    dispatch(initiateCallRequest(payload));
  };

  const handleCallDisconnect = useCallback(
    (payload: EndInitiatedCallPayload) => {
      holdCallHandlers?.cleanupHold?.();
      // Reset Call States
      setIncomingCallProspect(null);

      if (
        Number(payload?.callId) ===
        Number(currentCallManagementState?.acceptedCallId)
      ) {
        callIdRef.current = +(payload?.callId ?? null);
      }
      setIsCallConnected(false);
      dispatch(endInitiatedCallRequest(payload));
    },
    [currentCallManagementState?.acceptedCallId],
  );

  const handleDialerClose = useCallback(() => {
    if (!showIncomingCall) {
      setIncomingCallProspect(null);
    }
    setIsCallConnected(false);
    dispatch(setShowDialer(false));
  }, [showIncomingCall]);

  const handleMicrophoneAccessWarningModalClose = useCallback(() => {
    dispatch(setShowMicrophoneAccessWarningModal(false));
  }, [dispatch]);

  const copyStylesToPip = (win: Window) => {
    [...document?.styleSheets].forEach((styleSheet) => {
      try {
        const cssRules = [...styleSheet.cssRules]
          .map((rule) => rule.cssText)
          .join('');
        const style = win.document.createElement('style');

        style.textContent = cssRules;
        win.document.head.appendChild(style);
      } catch (e) {
        // Fallback for CORS/external stylesheets (link tags)
        const link = win.document.createElement('link');
        link.rel = 'stylesheet';
        link.type = styleSheet.type;
        link.media = styleSheet.media.mediaText;
        link.href = styleSheet.href;
        win.document.head.appendChild(link);
      }
    });
  };

  const managePipDialer = useCallback(async (pipDialer?: boolean) => {
    if (pipDialer) {
      dispatch(setShowDialer(false));
      setPipWindow(null);
      return;
    }

    if ('documentPictureInPicture' in window) {
      try {
        const pip = await window?.documentPictureInPicture?.requestWindow({
          width: 332,
          height: 600,
        });

        copyStylesToPip(pip);

        // Detect when user manually closes the PiP window (Move to Tab or Close X)
        pip.addEventListener('pagehide', () => {
          setPipWindow(null);
          setPipClosedUnexpectedly(true);
        });

        setPipWindow(pip);
      } catch (err) {
        console.error('Failed to open PiP:', err);
        setPipWindow(null);
        setShowWithoutPip(true);
      }
    } else {
      setShowWithoutPip(true);
    }
  }, []);

  const handleEndCallConfirm = useCallback(() => {
    setShowEndCallConfirmModal(false);
    if (initiatedCallId) {
      handleCallDisconnect({ callId: initiatedCallId });
    }
    handleDialerClose();
  }, [handleCallDisconnect, handleDialerClose, initiatedCallId]);

  const handleEndCallCancel = useCallback(() => {
    setShowEndCallConfirmModal(false);
    managePipDialer(); // Reopen PIP at the same call stage
  }, [managePipDialer]);

  const handleCallConnection = useCallback(
    async (call: Call) => {
      try {
        if (call) {
          console.log({ call });
          const autoAnswer =
            call?.customParameters?.get('autoAnswer') === 'true';
          const sessionHashId = call?.customParameters?.get('sessionHashId');
          const isRecordingEnabled =
            call?.customParameters?.get('isRecordingEnabled') === 'true';
          const incomingPhoneNumber = call?.customParameters?.get(
            'prospectNumber',
          );
          console.log({ incomingPhoneNumber });
          const prospectName =
            call?.customParameters?.get('prospectName') ||
            incomingPhoneNumber ||
            'Unknown Prospect';
          const callId = call?.customParameters?.get('callId');

          if (isRecordingEnabled) {
            dispatch(enableCallRecording());
          }
          if (prospectName) {
            const [firstName, lastName] = prospectName?.split(/ (.+)/);
            setIncomingCallProspect({
              firstName,
              lastName,
              incomingPhoneNumber,
            });
          }

          if (autoAnswer && sessionHashId === twillioVoiceTokenSessionHashId) {
            dispatch(setCallNetworkLevel(4));
            await call?.accept();
            if (callId) {
              dispatch(pickupCallRequest({ callId: Number(callId) }));
              if (!showDialerRef.current) {
                // In case of refresh or reconnect showing popup automatically with state
                dispatch(setInitiatedCallId(callId));
                dispatch(setShowDialer(true));
                setIsCallConnected(true);
              }
            }
            setCallManagementState((prev) => ({
              ...prev,
              callTwilioStatus: 'connected',
              call,
              callDirection: 'outbound',
              callIsInprogress: true,
              acceptedCallId: Number(callId),
            }));
          }

          // ** If sessionHashId is missing in twilio then do the logic for incoming call.
          if (!sessionHashId) {
            if (callId) {
              dispatch(setInitiatedCallId(callId));
            }
            // If inbound call is then only show the Call Incoming popup
            dispatch(setShowIncomingCall(true));
            setCallManagementState((prev) => ({
              ...prev,
              call,
              callDirection: 'inbound',
            }));
          }
        }
      } catch (error) {
        setCallManagementState((prev) => ({
          ...prev,
          callTwilioStatus: 'error',
          callIsInprogress: false,
          callDirection: null,
          call: null,
          acceptedCallId: null,
        }));
        setIncomingCallProspect(null);
        console.error('Error accepting call!', error);
      }
    },
    [twillioVoiceTokenSessionHashId],
  );

  const initiateTwilioService = useCallback(
    async (voiceToken: string) => {
      if (!twilioDeviceRef.current) {
        twilioDeviceRef.current = new TwilioDevice(voiceToken);
        registerTwilioDevice(twilioDeviceRef.current);

        const device = twilioDeviceRef.current;

        device.on('registered', () => {
          setCallManagementState((prev) => ({
            ...prev,
            isTwilioServiceRegistered: true,
          }));
        });

        device.on('error', (error) => {
          setCallManagementState((prev) => ({
            ...prev,
            errorInTwilioService: error,
            callTwilioStatus: 'error',
            callIsInprogress: false,
            call: null,
          }));
        });

        device.on('incoming', async (call) => {
          call.on('cancel', () => {
            dispatch(setShowIncomingCall(false));
            setIncomingCallProspect(null);
            console.error('Call cancelled by Twilio');
          });

          call.on('error', (e) => {
            dispatch(setShowIncomingCall(false));
            setIncomingCallProspect(null);
            toaster.error('Call connection error');
            console.error('Call error', e);
          });
          call.on('reconnecting', (error) => {
            console.debug('[Twilio] Call reconnecting...', error?.message);
            dispatch(setCallNetworkLevel(0));
            setCallManagementState((prev) => ({
              ...prev,
              callTwilioStatus: 'reconnecting',
            }));
          });

          call.on('reconnected', () => {
            dispatch(setCallNetworkLevel(4));
            setCallManagementState((prev) => ({
              ...prev,
              callTwilioStatus: 'connected',
            }));
          });

          call.on('warning', (warningName: string) => {
            dispatch(setCallNetworkLevel(warningToLevel(warningName)));
          });

          call.on('warning-cleared', () => {
            dispatch(setCallNetworkLevel(4));
          });

          await handleCallConnection(call);
        });

        device.on('disconnected', () => {
          setIncomingCallProspect(null);
          setCallManagementState((prev) => ({
            ...prev,
            callIsInprogress: false,
            callTwilioStatus: 'disconnected',
            call: null,
          }));
        });

        device.on('tokenWillExpire', () => {
          console.debug('[Twilio] Token will expire soon, refreshing token...');
          dispatch(createTwillioVoiceTokenRequest());
        });

        // Register the device with Twilio
        await device.register();
      }
    },
    [twillioVoiceTokenSessionHashId, twilioDeviceRef?.current],
  );

  const handleIncomingAnswer = useCallback(async () => {
    dispatch(setShowIncomingCall(false));
    holdCallHandlers?.cleanupHold?.();
    const call = callManagementState?.call;
    if (call) {
      await call.accept();

      if (initiatedCallId) {
        dispatch(pickupCallRequest({ callId: Number(initiatedCallId) }));
      }
      dispatch(setShowDialer(true));
      setIsCallConnected(true);
      setCallManagementState((prev) => ({
        ...prev,
        callTwilioStatus: 'connected',
        callStatus: 'answered',
        callIsInprogress: true,
        acceptedCallId: Number(initiatedCallId),
      }));
    }
  }, [
    callManagementState?.call,
    setIsCallConnected,
    initiatedCallId,
    holdCallHandlers,
  ]);

  const handleIncomingDecline = useCallback(() => {
    dispatch(setShowIncomingCall(false));
    if (initiatedCallId) {
      handleCallDisconnect({
        callId: initiatedCallId?.toString(),
      });
    }
  }, [initiatedCallId, handleCallDisconnect]);

  const handlePostCallModalDone = useCallback(
    ({
      outcomeId,
      noteContent,
      noteVisibility: nv,
      attachmentIds,
    }: {
      outcomeId: number | null;
      noteContent: string;
      noteVisibility: visibility;
      attachmentIds: number[];
    }) => {
      const hasNotePayload =
        noteContent.trim() !== '' || (attachmentIds?.length ?? 0) > 0;
      pendingCallsRef.current = {
        outcome: outcomeId !== null,
        note: hasNotePayload,
      };
      if (outcomeId !== null && showPostCallModal !== null) {
        dispatch(
          assignCallOutcomeRequest({
            outcomeId,
            dialerCallLogId: showPostCallModal,
          }),
        );
      }
      if (hasNotePayload && showPostCallModal !== null) {
        dispatch(
          createCallLogsNoteRequest({
            callId: showPostCallModal,
            content: noteContent,
            visibility: nv,
            status: 1,
            attachmentIds: attachmentIds ?? [],
          }),
        );
      }
      setShowPostCallModal(null);
      setPostCallData(null);
      setIsCallConnected(false);
      dispatch(clearDuringCallNote());
      dispatch(resetGetCallLogsProspectProfileSectionRequest());
      window.dispatchEvent(new CustomEvent('sh:call-ended'));
    },
    [dispatch, showPostCallModal],
  );

  const handleVoicemailDropped = useCallback((callId: number) => {
    setShowPostCallModal(callId);
  }, []);

  const handlePostCallModalCall = useCallback(() => {
    // This functiona will set the call Id when User disconnect the call
    if (
      +initiatedCallId === Number(currentCallManagementState?.acceptedCallId)
    ) {
      setShowPostCallModal(+initiatedCallId);
    }
  }, [initiatedCallId, currentCallManagementState?.acceptedCallId]);

  useEffect(() => {
    if (+showPostCallModal) {
      // Fetch the data for Call Outcome
      dispatch(getCallLogsProspectProfileSectionRequest(+showPostCallModal));
    }
  }, [showPostCallModal]);

  useEffect(() => {
    executeOnRequestStatus({
      status: profileSectionRequestStatus,
      onSuccess: () => {
        if (profileSectionResponse && showPostCallModal !== null) {
          setPostCallData({
            callerName: profileSectionResponse?.prospect
              ? `${profileSectionResponse?.prospect?.firstName} ${profileSectionResponse?.prospect?.lastName}`.trim()
              : null,
            callerPhone:
              profileSectionResponse?.prospect?.phone ??
              (profileSectionResponse?.type === 'inbound'
                ? profileSectionResponse?.originFromNumber ||
                  profileSectionResponse?.fromNumber
                : profileSectionResponse?.toNumber) ??
              '',
            isIncomingCall: profileSectionResponse?.type === 'inbound',
            callViaLabel:
              profileSectionResponse.dialerPhoneNumber?.friendlyName ?? null,
            callViaNumber:
              profileSectionResponse.dialerPhoneNumber?.phoneNumber ?? '',
            wasRecording: dialerControlsState?.record ?? false,
            completedAt: profileSectionResponse?.completedAt ?? null,
            callDuration: profileSectionResponse?.callDuration ?? null,
            prospectId: profileSectionResponse?.prospect?.id ?? null,
          });
        }
      },
    });
  }, [profileSectionRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: initiateCallRequestStatus,
      onPending: () => {
        if (showPostCallModal !== null) {
          setShowPostCallModal(null);
          setPostCallData(null);
          dispatch(resetGetCallLogsProspectProfileSectionRequest());
        }
      },
    });
  }, [initiateCallRequestStatus]);

  useEffect(() => {
    if (twillioVoiceToken) {
      if (twilioDeviceRef?.current) {
        twilioDeviceRef?.current?.updateToken(twillioVoiceToken);
      } else {
        initiateTwilioService(twillioVoiceToken);
      }
    }
  }, [twillioVoiceToken]);

  useEffect(() => {
    if (firebaseData?.status && !isConnectionPending) {
      const { status } = firebaseData;
      const statusLabel = DialerPhoneNumberCallStatusLabel?.[status] || null;
      setCallManagementState((prev) => ({
        ...prev,
        callStatus: statusLabel,
      }));

      if (
        status === DialerPhoneNumberCallStatusEnum.COMPLETED ||
        status === DialerPhoneNumberCallStatusEnum.FAILED
      ) {
        holdCallHandlers?.cleanupHold?.();
        if (status === DialerPhoneNumberCallStatusEnum.COMPLETED) {
          handlePostCallModalCall();
        } else {
          // Call failed (no answer, busy, etc.) — no post-call modal
          window.dispatchEvent(new CustomEvent('sh:call-ended'));
        }
        dispatch(clearInitiatedCallId());
        dispatch(setShowIncomingCall(false));
        setIncomingCallProspect(null);
      }
    }
    if (isConnectionPending) {
      setCallManagementState((prev) => ({
        ...prev,
        callStatus: 'initiated',
      }));
    }
  }, [firebaseData?.status, isConnectionPending]);

  useEffect(() => {
    if (showDialer) {
      dispatch(getDialerPhoneNumberListRequest({}));
      managePipDialer(); // Enable PIP
    }
  }, [showDialer, dispatch]);

  // Close PIP window (or inline fallback) when dialer is hidden
  useEffect(() => {
    if (!showDialer) {
      if (pipWindow) {
        pipWindow.close();
        setPipWindow(null);
      }
      setShowWithoutPip(false);
    }
  }, [showDialer, pipWindow]);
  useEffect(() => {
    if (selectedCallbackProspect?.length > 0) {
      dispatch(setShowDialer(true));
    }
  }, [selectedCallbackProspect, dispatch]);

  useEffect(() => {
    const { callStatus } = callManagementState;
    if (callStatus === 'initiated' || callStatus === 'ringing') {
      playOutboundRingtone();
    } else {
      stopOutboundRingtone();
    }
  }, [callManagementState?.callStatus, callManagementState?.callIsInprogress]);

  useEffect(() => () => stopOutboundRingtone(), []);

  useEffect(() => {
    if (callManagementState?.call) {
      registerActiveTwilioCall(callManagementState?.call);
    }
  }, [callManagementState?.call]);

  // Warn the user before refreshing/closing the main tab while a call is live.
  // PiP window close has its own confirmation flow and is intentionally separate.
  useEffect(() => {
    const isCallLive = isOngoingDialerCall({
      callIsInprogress: callManagementState?.callIsInprogress,
      callStatus: callManagementState?.callStatus,
    });
    if (!isCallLive) return undefined;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [callManagementState?.callIsInprogress, callManagementState?.callStatus]);

  // Set current call management state in Redux
  useEffect(() => {
    dispatch(setCurrentCallManagementState(callManagementState));
  }, [callManagementState, dispatch]);

  // show dialer stale ref to avoid callback dependancy
  useEffect(() => {
    showDialerRef.current = showDialer;
  }, [showDialer]);

  // When PIP closes (pagehide), decide whether to show confirmation or close silently
  useEffect(() => {
    if (!pipClosedUnexpectedly) return;
    setPipClosedUnexpectedly(false);
    if (
      isOngoingDialerCall({
        callIsInprogress: currentCallManagementState?.callIsInprogress,
        callStatus: currentCallManagementState?.callStatus,
      })
    ) {
      setShowEndCallConfirmModal(true);
    } else {
      handleDialerClose();
    }
  }, [
    pipClosedUnexpectedly,
    currentCallManagementState?.callIsInprogress,
    currentCallManagementState?.callStatus,
    handleDialerClose,
  ]);

  // Track call start time so DialerPopup can resume the timer correctly after PIP reopen
  useEffect(() => {
    const isConnected =
      callManagementState?.callTwilioStatus === 'connected' &&
      callManagementState?.callStatus === 'answered';
    if (isConnected) {
      setCallConnectedAt((prev) => prev ?? Date.now());
    } else if (!callManagementState?.callIsInprogress) {
      setCallConnectedAt(null);
    }
  }, [
    callManagementState?.callTwilioStatus,
    callManagementState?.callStatus,
    callManagementState?.callIsInprogress,
  ]);

  // Call connect/disconnect toasters effects
  useEffect(() => {
    executeOnRequestStatus({
      status: endInitiatedCallRequestStatus,
      onSuccess: () => {
        toaster.success('Call Disconnected');
        if (callIdRef?.current) {
          setShowPostCallModal(+callIdRef?.current);
          callIdRef.current = null;
        } else {
          // Unanswered call ended — no post-call modal will show
          window.dispatchEvent(new CustomEvent('sh:call-ended'));
        }
      },
      onFailed: () => {
        callIdRef.current = null;
        toaster.error('Failed to disconnect the call');
        window.dispatchEvent(new CustomEvent('sh:call-ended'));
      },
    });
  }, [endInitiatedCallRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: initiateCallRequestStatus,
      onSuccess: () => {
        toaster.success('Call initiated successfully');
      },
    });
  }, [initiateCallRequestStatus]);

  useEffect(() => {
    const { outcome, note } = pendingCallsRef.current;
    if (!outcome && !note) return;

    const outcomeSettled =
      !outcome ||
      assignCallOutcomeStatus === RequestStatus.Succeeded ||
      assignCallOutcomeStatus === RequestStatus.Failed;
    const noteSettled =
      !note ||
      createNoteStatus === RequestStatus.Succeeded ||
      createNoteStatus === RequestStatus.Failed;

    if (!outcomeSettled || !noteSettled) return;

    const outcomeFailed =
      outcome && assignCallOutcomeStatus === RequestStatus.Failed;
    const noteFailed = note && createNoteStatus === RequestStatus.Failed;

    if (outcomeFailed && noteFailed) {
      toaster.error('Failed to save call outcome and note');
    } else if (outcomeFailed) {
      toaster.error('Failed to save call outcome');
    } else if (noteFailed) {
      toaster.error('Failed to save note');
    } else {
      toaster.success('Response saved successfully');
    }

    pendingCallsRef.current = { outcome: false, note: false };
  }, [assignCallOutcomeStatus, createNoteStatus]);

  return (
    <>
      {
        // eslint-disable-next-line no-nested-ternary
        pipWindow !== null ? (
          <PipWindow pipWindow={pipWindow} className="bs-pip-dialer">
            <DialerPopup
              isInPip
              pipContainer={pipWindow.document.body}
              dialerPhoneNumberList={dialerPhoneNumberList}
              onCallClick={handleCallClick}
              onCallDisconnect={handleCallDisconnect}
              initiateCall={true}
              initiateRinging={false}
              onClose={handleDialerClose}
              initiatedCallId={initiatedCallId}
              callManagementState={callManagementState}
              setCallManagementState={setCallManagementState}
              holdCallHandlers={holdCallHandlers ?? null}
              onPreCallTestClick={() => setShowPreCallTestModal(true)}
              selectedCallbackProspect={selectedCallbackProspect}
              isCallConnected={isCallConnected}
              setIsCallConnected={setIsCallConnected}
              incomingCallProspect={incomingCallProspect}
              callConnectedAt={callConnectedAt}
              onVoicemailDropped={handleVoicemailDropped}
            />
          </PipWindow>
        ) : showWithoutPip ? (
          <DialerPopup
            dialerPhoneNumberList={dialerPhoneNumberList}
            onCallClick={handleCallClick}
            onCallDisconnect={handleCallDisconnect}
            initiateCall={true}
            initiateRinging={false}
            onClose={handleDialerClose}
            initiatedCallId={initiatedCallId}
            callManagementState={callManagementState}
            setCallManagementState={setCallManagementState}
            holdCallHandlers={holdCallHandlers ?? null}
            onPreCallTestClick={() => setShowPreCallTestModal(true)}
            selectedCallbackProspect={selectedCallbackProspect}
            isCallConnected={isCallConnected}
            setIsCallConnected={setIsCallConnected}
            incomingCallProspect={incomingCallProspect}
            callConnectedAt={callConnectedAt}
            onVoicemailDropped={handleVoicemailDropped}
          />
        ) : null
      }
      {showIncomingCall && (
        <DialerIncomingCallWrapper
          prospect={incomingCallProspect}
          onAnswer={handleIncomingAnswer}
          onDecline={handleIncomingDecline}
        />
      )}
      {showPreCallTestModal && (
        <PreCallTestModal
          show={showPreCallTestModal}
          onClose={() => setShowPreCallTestModal(false)}
          onDone={() => setShowPreCallTestModal(false)}
          {...preCallTestProps}
        />
      )}
      {showMicrophoneAccessWarningModal && (
        <MicrophoneAccessWarningModal
          show={showMicrophoneAccessWarningModal}
          onClose={handleMicrophoneAccessWarningModalClose}
        />
      )}
      {showPostCallModal !== null && (
        <PostCallOutcomeModal
          show={showPostCallModal !== null}
          isLoading={profileSectionRequestStatus === RequestStatus.Pending}
          callId={showPostCallModal}
          prospectId={postCallData?.prospectId ?? null}
          callerName={postCallData?.callerName ?? null}
          callerPhone={postCallData?.callerPhone ?? ''}
          isIncomingCall={postCallData?.isIncomingCall ?? false}
          callViaLabel={postCallData?.callViaLabel ?? null}
          callViaNumber={postCallData?.callViaNumber ?? ''}
          wasRecording={postCallData?.wasRecording ?? false}
          completedAt={postCallData?.completedAt ?? null}
          callDuration={postCallData?.callDuration ?? null}
          outcomeList={outcomeList}
          initialNote={duringCallNote}
          onDone={handlePostCallModalDone}
        />
      )}
      {showEndCallConfirmModal && (
        <ConfirmationModal
          show={showEndCallConfirmModal}
          onClose={handleEndCallCancel}
          customHeaderText="End the active call?"
          CustomContent="Closing the dialer will disconnect the ongoing call. Are you sure you want to end it?"
          onSubmit={handleEndCallConfirm}
          showCloseIcon={false}
        />
      )}
    </>
  );
};

export default DialerManager;
